import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { memoryApi, memoryClassApi } from '@/api/cortex.api';
import { Button } from '@/components/ui/button';
import { UserName } from '@/components/UserName';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { MemorySchema, type Memory } from '@/proto/agentplatform/v1/memory_pb';
import { MemoryClassSchema, type MemoryClass } from '@/proto/agentplatform/v1/memory_pb';
import { LoaderIcon, PencilIcon, TrashIcon } from '@/components/icons';
import { formatTimestamp } from '@/utils/utils';

type TabId = 'overview' | 'instances';
type FormState = { name: string; description: string; interactionMode: string };

function OverviewTab({ classData }: { classData: MemoryClass }) {
  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">基本信息</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <p className="text-xs text-muted-foreground">类名称</p>
              <p className="font-medium mt-1">{classData.name}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">描述</p>
            <p className="text-sm mt-1">{classData.description || '暂无描述'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">交互模式</p>
            <Badge variant="outline" className="text-xs mt-1">
              {classData.template?.interactionMode || 'AUTO_EXTRACT_AND_TOOL'}
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-muted-foreground">记忆库模版 ID</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all mt-1 inline-block">
                {classData.id}
              </code>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">租户 ID</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all mt-1 inline-block">
                {classData.tenantId}
              </code>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">用户 ID</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all mt-1 inline-block">
                <UserName userId={classData.userId} />
              </code>
            </div>
          </div>
        </CardContent>
      </Card>

      {classData.params.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">参数配置</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">名称</th>
                    <th className="px-4 py-2.5 text-left font-medium">类型</th>
                    <th className="px-4 py-2.5 text-left font-medium">目标</th>
                    <th className="px-4 py-2.5 text-left font-medium">值</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {classData.params.map((param, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2.5 font-mono text-xs">{param.name}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-xs">
                          {param.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{param.target}</td>
                      <td className="px-4 py-2.5 text-xs">
                        {param.paramValue.case === 'value' ? param.paramValue.value : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            暂无参数配置
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function InstancesTab({
  instancesData,
  loading,
  page,
  pageSize,
  setPage,
  setPageSize,
  onOpenMemoryManager,
}: {
  instancesData: { total: number; data: Memory[] } | undefined;
  loading: boolean;
  page: number;
  pageSize: number;
  setPage: (next: number) => void;
  setPageSize: (next: number) => void;
  onOpenMemoryManager: (memory: Memory) => void;
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }
  const rows = instancesData?.data || [];
  const total = instancesData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          该记忆库模版暂无实例
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">实例列表</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">名称</th>
                <th className="px-4 py-2.5 text-left font-medium">更新时间</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      className="font-medium text-primary hover:text-primary/80"
                      onClick={() => onOpenMemoryManager(row)}
                    >
                      {row.name}
                    </button>
                    <div className="text-xs text-muted-foreground">ID: {row.id}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {formatTimestamp(row.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">共 {total} 条</span>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="h-8 rounded-md border px-2"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              上一页
            </Button>
            <span>
              {page}/{totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MemoryClassDetailPage() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState<FormState>({
    name: '',
    description: '',
    interactionMode: 'AUTO_EXTRACT_AND_TOOL',
  });
  const [instancesPage, setInstancesPage] = useState(1);
  const [instancesPageSize, setInstancesPageSize] = useState(10);

  const classQuery = useQuery({
    queryKey: ['memory-class', uid],
    queryFn: () => memoryClassApi.getMemoryClass(uid!),
    enabled: !!uid,
  });

  const instancesQuery = useQuery({
    queryKey: ['memory-instances-by-class', uid, instancesPage, instancesPageSize],
    queryFn: () =>
      memoryApi.listMemories(
        create(ListRequestSchema, {
          page: instancesPage,
          pageSize: instancesPageSize,
          filter: { class_id: uid || '' },
        })
      ),
    enabled: !!uid,
  });

  const deleteMutation = useMutation({
    mutationFn: () => memoryClassApi.deleteMemoryClass(uid!),
    onSuccess: () => navigate('/plugins/memories?tab=class'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: MemoryClass) => memoryClassApi.updateMemoryClass(uid!, payload),
    onSuccess: () => {
      setShowEdit(false);
      queryClient.invalidateQueries({ queryKey: ['memory-class', uid] });
      queryClient.invalidateQueries({ queryKey: ['memory-classes'] });
    },
  });

  const classData = classQuery.data;
  if (classQuery.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (!classData) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          记忆库模版不存在或已删除
        </CardContent>
      </Card>
    );
  }

  const submitEdit = () => {
    const baseTemplate = classData.template;
    const template = create(MemorySchema, {
      id: baseTemplate?.id || '',
      name: baseTemplate?.name || '',
      description: baseTemplate?.description || '',
      tenantId: baseTemplate?.tenantId || '',
      userId: baseTemplate?.userId || '',
      classId: baseTemplate?.classId || '',
      classParams: baseTemplate?.classParams || {},
      provider: 'default',
      storeId: baseTemplate?.storeId || '',
      interactionMode: form.interactionMode || 'AUTO_EXTRACT_AND_TOOL',
    });
    const payload = create(MemoryClassSchema, {
      id: classData.id,
      name: form.name.trim(),
      description: form.description.trim(),
      tenantId: classData.tenantId,
      userId: classData.userId,
      template,
      params: classData.params,
      createdAt: classData.createdAt,
      updatedAt: classData.updatedAt,
      deletedAt: classData.deletedAt,
    });
    updateMutation.mutate(payload);
  };

  const handleOpenMemoryManager = (memory: Memory) => {
    localStorage.setItem('memory.activeStoreId', memory.id);
    navigate('/plugins/memories?tab=instance');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => navigate('/plugins/memories?tab=class')}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{classData.name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {classData.description || '暂无描述'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setForm({
                name: classData.name,
                description: classData.description || '',
                interactionMode: classData.template?.interactionMode || 'AUTO_EXTRACT_AND_TOOL',
              });
              setShowEdit(true);
            }}
          >
            <PencilIcon className="mr-1.5 h-3.5 w-3.5" />
            编辑
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setShowDelete(true)}
          >
            <TrashIcon className="mr-1.5 h-3.5 w-3.5" />
            删除
          </Button>
        </div>
      </div>

      <Tabs
        tabs={[
          { id: 'overview', label: '概览' },
          { id: 'instances', label: '实例' },
        ]}
        activeTab={activeTab}
        onChange={(id) => setActiveTab(id as TabId)}
      />

      {activeTab === 'overview' && <OverviewTab classData={classData} />}
      {activeTab === 'instances' && (
        <InstancesTab
          instancesData={instancesQuery.data}
          loading={instancesQuery.isLoading}
          page={instancesPage}
          pageSize={instancesPageSize}
          setPage={setInstancesPage}
          setPageSize={setInstancesPageSize}
          onOpenMemoryManager={handleOpenMemoryManager}
        />
      )}

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>编辑记忆库模版</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">名称 *</p>
              <Input
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">描述</p>
              <Input
                value={form.description}
                onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">交互模式</p>
              <select
                value={form.interactionMode}
                onChange={(e) => setForm((s) => ({ ...s, interactionMode: e.target.value }))}
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="TOOL">TOOL - 仅通过工具调用提取记忆和检索记忆</option>
                <option value="AUTO_EXTRACT_AND_TOOL">
                  AUTO_EXTRACT_AND_TOOL - 自动提取记忆、通过工具调用检索记忆
                </option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>
              取消
            </Button>
            <Button onClick={submitEdit} disabled={!form.name.trim() || updateMutation.isPending}>
              {updateMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除记忆库模版「{classData.name}」吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
